import * as yup from 'yup';
import { intervalToDuration } from 'date-fns';
import { DDL } from 'types/general.types';
import { Genre, Song, SongDDL, SongPatchValidationSchema } from 'types/song';
import { fieldValidationMessages } from 'app/constants';
import { omitNull } from 'app/utils/utils';
import { stringValidation } from 'app/utils/validations';
import { SongDetailsFormData } from './types';

export const getDefaultValues: (
  song: Song,
  languageCodes: DDL,
  songDDL: SongDDL
) => SongDetailsFormData = (song, languageCodes, { keySignatures }) => ({
  altTitle: song.altTitle || '',
  displayArtistName: song.displayArtistName || '',
  duration: song.duration || '',
  creationDate: song.creationDate?.date
    ? new Date(song.creationDate.date)
    : null,
  explicit: song.explicit || false,
  keySignature: song.keySignature
    ? {
        title: keySignatures[song.keySignature].name,
        value: keySignatures[song.keySignature].key,
      }
    : null,
  timeSignature: song.timeSignature
    ? {
        title: song.timeSignature,
        value: song.timeSignature,
      }
    : null,
  language: song.language
    ? {
        title: languageCodes[song.language].name,
        value: languageCodes[song.language].key,
      }
    : null,
  notes: song.notes || '',
  tempo: song.tempo || '',
  location: {
    address: song.location?.address || '',
  },
});

export const numberValidation = (min: number, max: number) =>
  yup.lazy((value) =>
    value === ''
      ? yup.string()
      : yup
          .number()
          .typeError(fieldValidationMessages.TYPE_NUM)
          .min(min, fieldValidationMessages.MIN_NUM(min))
          .max(max, fieldValidationMessages.MAX_NUM(max))
  );

export const getDurationFormat = (time: number) => {
  const maxDuration = intervalToDuration({ start: 0, end: time * 1000 });
  const hours = (maxDuration?.days || 0) * 24 + (maxDuration?.hours || 0);
  const minutes = maxDuration.minutes || 0;
  const seconds = maxDuration.seconds || 0;
  const hh = `${hours < 10 ? 0 : ''}${hours}`;
  const mm = `${minutes < 10 ? 0 : ''}${minutes}`;
  const ss = `${seconds < 10 ? 0 : ''}${seconds}`;
  return `${hh}:${mm}:${ss}`;
};

export const durationValidation = (min: number, max: number) => {
  const minFormat = getDurationFormat(min);
  const maxFormat = getDurationFormat(max);

  return yup.lazy((value) =>
    value === ''
      ? yup.string().trim()
      : yup
          .number()
          .typeError(fieldValidationMessages.TYPE_NUM)
          .min(min, `Min is ${minFormat}`)
          .max(max, `Max is ${maxFormat}`)
  );
};

export const getValidationSchema = (
  schema: SongPatchValidationSchema,
  song?: Song
) => {
  const {
    altTitle,
    displayArtistName,
    tempo,
    notes,
    duration,
    location: {
      properties: { address },
    },
  } = schema;

  return yup.object().shape({
    altTitle: stringValidation(altTitle.maxLength),
    displayArtistName: stringValidation(displayArtistName.maxLength),
    tempo: numberValidation(tempo.minimum, tempo.maximum),
    notes: stringValidation(notes.maxLength),
    duration: durationValidation(duration.minimum, duration.maximum),
    creationDate: song?.creationDate?.date
      ? yup
          .date()
          .nullable()
          .required(fieldValidationMessages.REQUIRED)
          .typeError('Invalid Date Format')
      : yup.date().nullable().typeError('Invalid Date Format'),
    location: yup
      .object()
      .shape({ address: stringValidation(address.maxLength) }),
  });
};

export const getOptions = (
  data: { enum: Array<string | null> },
  DDL: DDL,
  sort = false
) => {
  const options = (data.enum || [])
    .filter(omitNull)
    .map((value) => ({ title: DDL[value]?.name || value, value }));

  return sort
    ? options.sort((a, b) => a.title.localeCompare(b.title))
    : options;
};

export const getGenreValues = (genres: Genre[] = []): string[] =>
  genres.map((genre) => genre.value);

export const isClearable = (options: Array<string | null> | string) => {
  if (!Array.isArray(options)) {
    return false;
  }
  return Boolean(
    options.findIndex((el) => el === null || el === 'null') !== -1
  );
};
